/**
 * ExpenseCategorySection - Renders expense categories with per-account inputs
 *
 * Extracted from ProjectionInputView for maintainability.
 * Shows expense categories with expandable account-level detail and inputs.
 */

import React from 'react';
import { DepartmentTemplate, ExpenseCategory, AccountDefinition } from '../../config/docTemplate';
import { AccountData, ROW_COLORS, CATEGORY_COLORS, INCOME_CATEGORIES } from './projectionInputTypes';
import { AccountExpenseInputCell } from './ProjectionInputCells';
import { CategoryAccountExpenses } from '../../hooks/useProjectionCalculations';

interface ExpenseCategorySectionProps {
  dept: DepartmentTemplate;
  category: ExpenseCategory;
  /** Per display-year column (oldest first): the gross each column's % is relative to */
  yearGross: number[];
  showInput?: boolean;
  projectedDeptGross?: number;
  // Expansion state
  isExpanded: boolean;
  onToggleCategory: (categoryKey: string) => void;
  // Account data per display-year column (oldest first)
  yearAccountDataMaps: Map<string, AccountData>[];
  // Calculation functions
  calculateCategoryTotals: (category: ExpenseCategory, accountDataMap: Map<string, AccountData>) => number;
  getAccountWithSign: (def: AccountDefinition, categoryName: string, accountDataMap: Map<string, AccountData>) => number;
  getCategoryAccountExpenses: (department: string, accounts: AccountDefinition[], projectedGross: number) => CategoryAccountExpenses;
  getAccountExpensePercent: (department: string, accountCode: string) => number | null;
  // Edit state
  isEditing: boolean;
  localInputs: Record<string, { value: number; inputType: 'amount' | 'percentage'; isDirty: boolean }>;
  // Callbacks
  formatCurrency: (value: number) => string;
  onValueChange: (department: string, metric: string, value: number, inputType?: 'amount' | 'percentage') => void;
}

/**
 * Format percentage for display
 */
const formatPercent = (value: number, gross: number): string => {
  if (gross === 0) return '--';
  const percent = (value / gross) * 100;
  return `${percent.toFixed(1)}%`;
};

/**
 * Renders an expense category with per-account inputs
 */
export const ExpenseCategorySection: React.FC<ExpenseCategorySectionProps> = ({
  dept,
  category,
  yearGross,
  showInput = true,
  projectedDeptGross = 0,
  isExpanded,
  onToggleCategory,
  yearAccountDataMaps,
  calculateCategoryTotals,
  getAccountWithSign,
  getCategoryAccountExpenses,
  getAccountExpensePercent,
  isEditing,
  localInputs,
  formatCurrency,
  onValueChange,
}) => {
  const categoryKey = `${dept.name}-${category.name}`;

  // Calculate category totals for each display-year column
  const ytdByYear = yearAccountDataMaps.map(map => calculateCategoryTotals(category, map));
  const lastIdx = yearAccountDataMaps.length - 1;

  const colorClass = CATEGORY_COLORS[category.name] || ROW_COLORS.otherExpense;

  // Get accounts with data (in any display year) for expansion
  const accountsWithData = category.accounts.filter(acc => yearAccountDataMaps.some(map => map.has(acc.code)));

  // All template accounts (for expanded rows - show accounts even without historical data)
  const allTemplateAccounts = category.accounts;

  if (ytdByYear.every(v => v === 0) && accountsWithData.length === 0 && category.accounts.length === 0) {
    return null;
  }

  // Determine if % column should show values
  const showPercentValue = !INCOME_CATEGORIES.includes(category.name) && category.name !== 'RENTAL_INCOME';

  // Calculate projected category totals from account-level inputs
  const categoryExpenses = getCategoryAccountExpenses(dept.name, allTemplateAccounts, projectedDeptGross);
  // Check if any accounts have projected values (totalPercent can be negative due to credits)
  const hasProjectedExpenses = categoryExpenses.totalPercent !== 0 || Object.keys(categoryExpenses.accountExpenses).length > 0;

  return (
    <React.Fragment key={categoryKey}>
      {/* Category Header Row - shows summed totals from account inputs */}
      <tr
        className={`${colorClass} border-b border-gray-200 ${allTemplateAccounts.length > 0 ? 'cursor-pointer hover:opacity-90' : ''}`}
        onClick={() => allTemplateAccounts.length > 0 && onToggleCategory(categoryKey)}
      >
        <td className={`min-w-[200px] px-3 py-2 text-sm sticky left-0 ${colorClass}`} style={{ paddingLeft: '28px' }}>
          <div className="flex items-center">
            {allTemplateAccounts.length > 0 && (
              <span className="mr-2 text-gray-500 w-4 flex-shrink-0">
                {isExpanded ? '▼' : '▶'}
              </span>
            )}
            {allTemplateAccounts.length === 0 && <span className="mr-2 w-4 flex-shrink-0"></span>}
            <span>{category.displayName}</span>
            {allTemplateAccounts.length > 0 && (
              <span className="ml-2 text-xs text-gray-500">({allTemplateAccounts.length})</span>
            )}
          </div>
        </td>
        {ytdByYear.map((ytd, i) => (
          <React.Fragment key={i}>
            <td className={`min-w-[100px] px-2 py-2 text-right text-sm ${i === lastIdx ? 'font-semibold' : ''}`}>
              {formatCurrency(ytd)}
            </td>
            {showPercentValue && (
              <td className="min-w-[60px] px-1 py-2 text-right text-xs text-gray-500">
                {formatPercent(ytd, yearGross[i] || 0)}
              </td>
            )}
          </React.Fragment>
        ))}
        {/* Category total - auto-calculated from account inputs */}
        <td className={`min-w-[120px] px-2 py-2 text-right text-sm ${colorClass}`}>
          {showInput && hasProjectedExpenses ? (
            <div className="whitespace-nowrap">
              <span className="font-bold">{formatCurrency(categoryExpenses.totalAmount)}</span>
              <span className="text-xs ml-1">{categoryExpenses.totalPercent.toFixed(1)}%</span>
            </div>
          ) : showInput ? '--' : ''}
        </td>
      </tr>

      {/* Expanded Account Rows with input fields */}
      {isExpanded && allTemplateAccounts.map(acc => {
        const valuesByYear = yearAccountDataMaps.map(map => getAccountWithSign(acc, category.name, map));
        const percentValue = getAccountExpensePercent(dept.name, acc.code);
        const key = `${dept.name}#EXPENSE_${acc.code}`;
        const isDirty = localInputs[key]?.isDirty || false;

        return (
          <tr key={acc.code} className="bg-gray-50 border-b border-gray-100 hover:bg-gray-100">
            <td className="min-w-[200px] px-3 py-1.5 text-xs sticky left-0 bg-gray-50" style={{ paddingLeft: '44px' }}>
              <div className="flex items-center">
                <span className="font-mono text-gray-600 mr-2">{acc.code}</span>
                <span className="text-gray-500 truncate">{acc.description}</span>
              </div>
            </td>
            {valuesByYear.map((v, i) => (
              <React.Fragment key={i}>
                <td className={`min-w-[100px] px-2 py-1.5 text-right text-xs ${i === lastIdx ? 'font-medium' : 'text-gray-700'}`}>
                  {formatCurrency(v)}
                </td>
                {showPercentValue && (
                  <td className="min-w-[60px] px-1 py-1.5 text-right text-xs text-gray-400">
                    {formatPercent(v, yearGross[i] || 0)}
                  </td>
                )}
              </React.Fragment>
            ))}
            {/* Account-level input */}
            {showInput ? (
              <AccountExpenseInputCell
                department={dept.name}
                accountCode={acc.code}
                percentValue={percentValue}
                projectedGross={projectedDeptGross}
                isEditing={isEditing}
                isDirty={isDirty}
                formatCurrency={formatCurrency}
                onValueChange={onValueChange}
              />
            ) : <td className="min-w-[120px] bg-gray-50"></td>}
          </tr>
        );
      })}
    </React.Fragment>
  );
};
